import { env } from "../../env";
import Turnstile from "./Turnstile";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
  TextField,
} from "@mui/material";
import { Formik, FormikHelpers, FormikValues } from "formik";
import React from "react";
import * as Yup from "yup";

// GDPR Art. 8 sets the digital-consent age at 16, which member states may
// lower to 13. A single global threshold avoids region detection, and a
// confirmation rather than a date of birth avoids collecting more personal
// data than the check actually needs.
export const MINIMUM_AGE = 16;

export interface PolicyVersion {
  documentType: string;
  version: number;
}

interface SignUpFormProps<T> {
  initialValues: FormikValues & T;
  onSubmit: (data: T, helpers: FormikHelpers<FormikValues & T>) => void;
  loading: boolean;
  /**
   * Published policies, owned by the parent.
   *
   * Deliberately a prop rather than a query in here. The parent needs the
   * same list to build the accepted versions it submits, and the generated
   * hooks default to `no-cache`, so two components querying independently
   * would not share a result. If this component resolved first, a user could
   * tick the box while the parent still submitted an empty list, and the
   * server would reject a registration the UI had shown as valid.
   */
  policies?: PolicyVersion[];
}

function SignUpForm<T>({
  initialValues,
  onSubmit,
  loading,
  policies,
}: SignUpFormProps<T>) {
  const turnstileEnabled = Boolean(env.REACT_APP_TURNSTILE_SITE_KEY);
  const policiesPublished = Boolean(policies?.length);
  const validationSchema = Yup.object().shape({
    accountName: Yup.string().max(150).required("Account name is required"),
    firstName: Yup.string().max(150).required("First name is required"),
    lastName: Yup.string().max(150).required("Last name is required"),
    email: Yup.string().max(150).required("Email is required"),
    password1: Yup.string().max(150).required("Password is required"),
    password2: Yup.string()
      .max(150)
      .required("Password confirmation is required")
      .test("passwords-match", "Passwords must match", function (value) {
        return this.parent.password1 === value;
      }),
    turnstileToken: turnstileEnabled
      ? Yup.string().required("Captcha verification is required")
      : Yup.string(),
    // Required only while a policy is published, matching the server, which
    // enforces the same condition. Marketing is deliberately absent: it is
    // optional and must never block registration.
    // `required` as well as `oneOf`: Yup's oneOf passes on `undefined`, so a
    // caller that omits these from initialValues would otherwise skip the
    // check entirely rather than fail it.
    acceptsPolicies: policiesPublished
      ? Yup.boolean()
          .required("You must accept the Terms of Service and Privacy Policy")
          .oneOf(
            [true],
            "You must accept the Terms of Service and Privacy Policy"
          )
      : Yup.boolean(),
    ageConfirmed: policiesPublished
      ? Yup.boolean()
          .required(`You must confirm you are ${MINIMUM_AGE} or older`)
          .oneOf([true], `You must confirm you are ${MINIMUM_AGE} or older`)
      : Yup.boolean(),
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({
        handleSubmit,
        handleChange,
        isSubmitting,
        setFieldValue,
        values,
        errors,
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Card sx={{ width: "100%" }}>
              <CardContent>
                <Grid container spacing={6}>
                  <Grid item xs={12} md={12}>
                    <TextField
                      type="text"
                      label="Business / Organization Name"
                      variant="outlined"
                      fullWidth
                      name="accountName"
                      value={values.accountName}
                      onChange={handleChange}
                      error={!!errors.accountName}
                      helperText={errors.accountName as string}
                      role="signup-account-name"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="First name"
                      variant="outlined"
                      fullWidth
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      error={!!errors.firstName}
                      helperText={errors.firstName as string}
                      role="signup-firstname"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Last name"
                      variant="outlined"
                      fullWidth
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      error={!!errors.lastName}
                      helperText={errors.lastName as string}
                      role="signup-lastname"
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      type="email"
                      label="Email"
                      variant="outlined"
                      fullWidth
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email as string}
                      role="signup-email"
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      type="password"
                      label="Password"
                      variant="outlined"
                      fullWidth
                      name="password1"
                      value={values.password1}
                      onChange={handleChange}
                      error={!!errors.password1}
                      helperText={errors.password1 as string}
                      role="signup-password1"
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      type="password"
                      label="Confirm password"
                      variant="outlined"
                      fullWidth
                      name="password2"
                      value={values.password2}
                      onChange={handleChange}
                      error={!!errors.password2}
                      helperText={errors.password2 as string}
                      role="signup-password2"
                    />
                  </Grid>
                  {/*
                    setFieldValue rather than handleChange: Formik infers a
                    checkbox's value from the input's `value` attribute, and
                    with MUI's Checkbox that yields an array rather than a
                    boolean, which then fails boolean validation.

                    Only rendered once a policy is published. Until then there
                    is nothing to accept, and registration works exactly as it
                    did before - the whole feature stays dormant.
                  */}
                  {policiesPublished && (
                    <Grid item xs={12} md={12}>
                      <FormControl
                        required
                        error={!!errors.acceptsPolicies}
                        component="fieldset"
                        variant="standard"
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="acceptsPolicies"
                              checked={Boolean(values.acceptsPolicies)}
                              onChange={(event) =>
                                setFieldValue(
                                  "acceptsPolicies",
                                  event.target.checked
                                )
                              }
                              data-testid="signup-accepts-policies"
                            />
                          }
                          label={
                            <span>
                              I agree to the{" "}
                              <Link
                                href="/legal/terms"
                                target="_blank"
                                rel="noopener"
                              >
                                Terms of Service
                              </Link>{" "}
                              and have read the{" "}
                              <Link
                                href="/legal/privacy"
                                target="_blank"
                                rel="noopener"
                              >
                                Privacy Policy
                              </Link>
                            </span>
                          }
                        />
                        {errors.acceptsPolicies && (
                          <FormHelperText>
                            {errors.acceptsPolicies as string}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                  {policiesPublished && (
                    <Grid item xs={12} md={12}>
                      <FormControl
                        required
                        error={!!errors.ageConfirmed}
                        component="fieldset"
                        variant="standard"
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="ageConfirmed"
                              checked={Boolean(values.ageConfirmed)}
                              onChange={(event) =>
                                setFieldValue(
                                  "ageConfirmed",
                                  event.target.checked
                                )
                              }
                              data-testid="signup-age-confirmed"
                            />
                          }
                          label={`I am ${MINIMUM_AGE} or older`}
                        />
                        {errors.ageConfirmed && (
                          <FormHelperText>
                            {errors.ageConfirmed as string}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                  {/*
                    Marketing is separate, optional, and unticked by default.
                    It is the only true consent on this form - freely given and
                    withdrawable - so it must never be bundled into the
                    required checkbox or block signing up.
                  */}
                  <Grid item xs={12} md={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="marketingOptIn"
                          checked={Boolean(values.marketingOptIn)}
                          onChange={(event) =>
                            setFieldValue(
                              "marketingOptIn",
                              event.target.checked
                            )
                          }
                          data-testid="signup-marketing-opt-in"
                        />
                      }
                      label="Send me product updates and news"
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Turnstile
                      error={errors.turnstileToken as string}
                      onTokenChange={(token) =>
                        setFieldValue("turnstileToken", token)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading || isSubmitting}
                      fullWidth
                      role="signup-button"
                    >
                      Sign Up
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </form>
        );
      }}
    </Formik>
  );
}

export default SignUpForm;
